import * as React from "react";

import { CamundaPlatformModeler as DmnModeler } from 'camunda-dmn-js';
import 'camunda-dmn-js/dist/assets/camunda-platform-modeler.css';

import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { State } from "processEditor/model/State";
import { IElementVariable, populateAccessibleReturnsAndVariables } from "processEditor/actions/VariableScopeAnalyzer";
import { ElementType } from "processEditor/model/IProcessData";
import { getTriggerReferences } from "processEditor/model/References";
import _ from "lodash";
import { create } from "xmlbuilder2";
import { updateParameter } from "processEditor/actions/UpdateParameter";
import { XMLBuilder } from "xmlbuilder2/lib/interfaces";
import DmnProvider from "../model/DmnCustomization/DmnProvider"
import CustomVariableProvider from "processEditor/model/DmnCustomization/CustomVariableProvider";
import ReduxStore from "processEditor/model/DmnCustomization/ReduxStore";
import styled from "styled-components";
import theme from "styled-theming";
import colors from "shared/styles/colors";
import { ThemeType } from "shared/styles/theme";

// There is a quite a few 'any' types in this file, so we'll disable the rule for now
/* eslint-disable @typescript-eslint/no-explicit-any */

const emptyDmnDefinition = `
<definitions xmlns="https://www.omg.org/spec/DMN/20191111/MODEL/" id="definitions" name="definitions" namespace="http://camunda.org/schema/1.0/dmn">
  <decision id="decisionId" name="">
    <decisionTable id="decisionTable">
      <output id="result" label="Result" name="Result" />
    </decisionTable>
  </decision>
</definitions>
`;

const getSanitizedVariableName = (variableName: string): string => {
    return variableName.replace(/[^a-zA-Z0-9]/g, '');
};

const addInputData = (inputDataRoot: XMLBuilder, requirementRoot: XMLBuilder, variables: string[]) => {
    for (const variable of variables) {
        const sanitized = getSanitizedVariableName(variable);
        const varId = `id_${sanitized}`;
        inputDataRoot.ele("inputData", { id: varId, name: variable.substring(1) });
        requirementRoot.ele("informationRequirement", { id: `ir_${sanitized}` }).ele("requiredInput", { href: `#${varId}` });
    }
};

const getDmnDefinition = (props: DmnProps, state: State): string => {
    const context = gatherContextForDmn(props, state);

    const dmnDefinition = create(props.existingParameters["dmnXml"] ?? emptyDmnDefinition);
    const definitionsRoot = dmnDefinition.root();
    const decisionRoot = definitionsRoot.first();

    // Set a unique id
    decisionRoot.att("id", `decisionId_${props.id}`);

    // Remove potential existing input data and information requirements
    definitionsRoot.filter((n) => n.node.nodeName === "inputData").forEach((n) => n.remove());
    decisionRoot.filter((n) => n.node.nodeName === "informationRequirement").forEach((n) => n.remove());

    // Add current input data and information requirements
    addInputData(definitionsRoot, decisionRoot, context.environmentVariables);
    addInputData(definitionsRoot, decisionRoot, context.otherResults);
    addInputData(definitionsRoot, decisionRoot, context.taskResults);
    addInputData(definitionsRoot, decisionRoot, context.triggers);
    addInputData(definitionsRoot, decisionRoot, context.variables);

    return dmnDefinition.toString();
};

const gatherContextForDmn = (props: DmnProps, state: State) => {
    const accessibleVariablesWithinScope: IElementVariable[] = [];
    const accessibleReturnsWithinScope: IElementVariable[] = [];

    populateAccessibleReturnsAndVariables(
        props.id!,
        state.validation.variableScopeDictionary,
        accessibleReturnsWithinScope,
        accessibleVariablesWithinScope);

    const elements = _.map(state.elements, (e) => e);
    const manualTriggerParameters = state.process.manualTrigger.map((x) => x.name);
    const triggerReferences = getTriggerReferences(elements.filter((x) => x.type === ElementType.Start), manualTriggerParameters, state.process.isForMonitoringRule);

    const taskResults = accessibleReturnsWithinScope
        .filter((x) => x.elementType === ElementType.Task)
        .map((x) => { return `#result[${x.variableName}]`; });

    return {
        variables: accessibleVariablesWithinScope.map(v => `#var.${v.variableName}`).concat(state.processVariables.map((v) => `#var.${v.name}`)),
        taskResults: taskResults,
        otherResults: accessibleReturnsWithinScope.filter((x) => x.elementType !== ElementType.Task).map(v => `#result[${v.variableName}]`),
        environmentVariables: state.environmentVariables
            .filter(v => v.includes(".")) // Don't send groups / root keys
            .map(v => `#env.${v}`),
        triggers: triggerReferences.map(v => `#trigger.${v}`)
    };
};

export const DmnEditor: React.FC<DmnProps> = (props) => {

    const dispatch = useDispatch();
    const dmnModeler = useRef<DmnModeler | null>(null);
    const state = useSelector<State, State>(state => state);
    const dmnDefinition = getDmnDefinition(props, state);
    const [isImportComplete, setIsImportComplete] = React.useState(false);

    const importDefinition = React.useCallback(async () => {
        try {
            const result = await dmnModeler.current!.importXML(dmnDefinition);
            const { warnings } = result;

            if (warnings?.length > 0) {
                console.warn('Warnings importing DMN XML: ', warnings);
            }
        } catch (err) {
            console.error('Error importing DMN XML: ', err.message, err.warnings);
        }

        setIsImportComplete(true);
    }, [dmnDefinition]);

    const getIdAndParameters = (xml: string): { decisionId: string; parameters: { [x: string]: string; }; } => {
        const parameters: { [x: string]: string; } = {};
        const xmlBuilder = create(xml).root();
        xmlBuilder
            .filter((n) => n.node.nodeName === "inputExpression", true, true)
            .flatMap((n) => n.node.textContent)
            .filter((n) => n)
            .forEach((n) => parameters[n!] = `#${n}`);

        // The node most definitely as an id, but it's apparently missing from the type definition
        const decisionId = (xmlBuilder.find((n) => n.node.nodeName === "decision")?.node as any)?.id;
        return { decisionId, parameters };
    };

    const removeUnusedVariables = (xml: string, parameters: { [x: string]: string; }) => {
        const xmlBuilder = create(xml).root();
        const inputDataIds: string[] = [];
        const inputRequirementIds: string[] = [];
        Object.entries(parameters).forEach(([, value]) => {
            const sanitized = getSanitizedVariableName(value);
            inputDataIds.push(`id_${sanitized}`);
            inputRequirementIds.push(`ir_${sanitized}`);
        });

        // Remove unused input data
        xmlBuilder
            .filter((n) => n.node.nodeName === "inputData", true, true)
            .filter((n) => !inputDataIds.includes((n.node as any).id))
            .forEach((n) => n.remove());

        // Remove unused information requirements
        xmlBuilder
            .filter((n) => n.node.nodeName === "informationRequirement", true, true)
            .filter((n) => !inputRequirementIds.includes((n.node as any).id))
            .forEach((n) => n.remove());

        return xmlBuilder.toString();
    };

    useEffect(() => {
        if (!dmnModeler.current) {
            dmnModeler.current = new DmnModeler({
                container: '#dmn-editor-canvas',
                width: '100%',
                height: '100%',
                mouse: {
                    bindTo: '#dmn-editor-canvas'
                },
                keyboard: {
                    bindTo: '#dmn-editor-canvas'
                },
                decisionTable: {
                    additionalModules: [
                        DmnProvider,
                        CustomVariableProvider,
                        { reduxStore: ["type", ReduxStore] },
                    ]
                },
            });

            importDefinition()
        }
    }, [importDefinition]);

    useEffect(() => {
        if (isImportComplete) {
            handleSaveDmn();
        }
    }, [state.uiState.dmnChangeCount]);

    const handleSaveDmn = async () => {
        try {
            const result = await dmnModeler.current!.saveXML({ format: false });
            const { xml } = result;
            const { decisionId, parameters } = getIdAndParameters(xml);

            // Remove unused variable references
            const cleanedXml = removeUnusedVariables(xml, parameters);

            dispatch(updateParameter(props.id, "dmnXml", cleanedXml) as any);
            dispatch(updateParameter(props.id, "decisionId", decisionId) as any);
            dispatch(updateParameter(props.id, "parameters", JSON.stringify(parameters)) as any);
        } catch (err) {
            console.error('Error saving DMN XML: ', err);
        }
    };

    return <DmnEditorCanvas id="dmn-editor-canvas" style={{ height: "100%" }} />
};

interface DmnProps {
    id: string;
    existingParameters: any;
}

/*
    These styles are originally based on this -> camunda-dmn-js\dist\assets\dmn-js-shared.css
    If/when the camunda-dmn-js library is updated, these styles might need to be updated as well.
    Unfortunately there doesn't seem to be a more elegant way to customize these styles.

    '.ͼ1 .variableName', '.ͼ1 .string' and '.ͼ1 .number' are used in some edit modes and the original colors clash really bad with our dark theme.
    '.ͼ2 .cm-tooltip' is the expression editor popup dialog.
*/
const colorOverrides = theme("mode", {
    [ThemeType.light]: "", // No changes for light mode
    [ThemeType.dark]: `
    div.dmn-js-parent {
        --color-grey-225-10-15: ${colors.grey_4};
        --color-grey-225-10-35: ${colors.grey_5};
        --color-grey-225-10-97: ${colors.dark5};
        --color-blue-205-100-35: ${colors.bluePrimary};
        --color-white: ${colors.dark};
        --color-black: ${colors.white};
    }
    .dmn-decision-table-container .context-menu-group .context-menu-group-entry:hover {
        background-color: ${colors.dark2};
    }
    .ͼ1 .variableName {
        color: ${colors.blueTertiary};
    }
    .ͼ1 .string {
        color: ${colors.orangeTertiary};
    }
    .ͼ1 .number {
        color: ${colors.greenTertiary};
    }
    .ͼ2 .cm-tooltip {
        background-color: ${colors.dark2};
    }`
});
const DmnEditorCanvas = styled.div`
    div.dmn-decision-table-container {
        min-height: 50vh;
    }
    div.view-drd, button.view-drd-button {
        display: none;
    }
    ${colorOverrides}
`;
